import { Injectable, computed, inject, signal } from '@angular/core';
import { DEMO_COLLABORATORS, DEMO_DEPARTMENTS, DEMO_MANAGER_TEAM } from '../../../core/demo/demo-data';
import type { AssetFamily } from '../../../core/models/asset-family.model';
import { familyOf } from '../../../core/models/category.model';
import {
  EMPLOYMENT_STATUS_CODES,
  type Collaborator,
  type CollaboratorRow,
  type Department,
  type EmploymentStatus,
  type EquipmentStatus,
  displayName,
  isPlausibleEmail,
} from '../../../core/models/collaborator.model';
import { SessionStore } from '../../../core/state/session.store';
import { InventoryApiService } from '../../inventory/api/inventory-api.service';

/** Charge utile de `POST /api/directory/collaborators`, alignée sur le contrat du backend. */
export interface CreateCollaboratorRequest {
  readonly lastName: string;
  readonly firstName: string;
  readonly email: string;
  readonly departmentId: string;
  /** 1 = À venir, 2 = Actif, 3 = Suspendu, 4 = Parti (RM-PIN-197). */
  readonly employmentStatus: 1 | 2 | 3 | 4;
  /** Format ISO `AAAA-MM-JJ`. */
  readonly arrivalDate: string;
  readonly managerId?: string | null;
  readonly jobTitle?: string | null;
  readonly internalNotes?: string | null;
}

/** Champs saisis dans le formulaire de création d'un collaborateur (UC-04). */
export interface NewCollaboratorDraft {
  readonly lastName: string;
  readonly firstName: string;
  readonly email: string;
  readonly departmentId: string;
  readonly employmentStatus: EmploymentStatus;
  /** Format ISO `AAAA-MM-JJ`, tel que le rend `<input type="date">`. */
  readonly arrivalDate: string;
  readonly managerId: string | null;
  readonly jobTitle: string;
  readonly internalNotes: string;
}

/** Échec de création, porteur du code de la règle violée. */
export interface CreateCollaboratorFailure {
  readonly code: string;
  readonly message: string;
  /** ERR-PIN-190 — fiche déjà porteuse de l'email saisi, pour proposer de l'ouvrir. */
  readonly existing?: CollaboratorRow;
}

/**
 * Annuaire des collaborateurs (SPEC-PIN-008).
 *
 * Les données viennent du jeu de démonstration : le backend n'expose pas encore de query de liste
 * exploitable (DIR-01 dépend du claim `teams`, donc de l'authentification) et sa base n'est pas
 * provisionnée.
 *
 * La création (UC-04) applique ici les mêmes règles que le handler livré et écrit dans l'état local,
 * afin que le parcours soit réellement praticable. La charge utile du vrai endpoint est construite
 * par {@link toCreateCollaboratorRequest} : le jour où `POST /api/directory/collaborators` est
 * joignable, c'est cet objet qui part tel quel. Deux prérequis manquent pour l'appeler aujourd'hui —
 * le jeton UniPortail (sans lui : 401) et une query de liste des services, seule façon d'obtenir un
 * `departmentId` réel (le backend refuse un service inexistant, ERR-PIN-191).
 *
 * Le périmètre par rôle est appliqué ici pour la démonstration ; en production il l'est **côté
 * serveur avant tout filtre et toute pagination** (RM-PIN-195). Le filtrer côté client ne serait pas
 * une mesure de sécurité.
 */
@Injectable({ providedIn: 'root' })
export class DirectoryApiService {
  private readonly session = inject(SessionStore);
  private readonly inventory = inject(InventoryApiService);

  private readonly _collaborators = signal<readonly Collaborator[]>(DEMO_COLLABORATORS);

  /** Référentiel des services, pour alimenter le champ de rattachement du formulaire. */
  readonly departments: readonly Department[] = DEMO_DEPARTMENTS;

  /** Annuaire complet, hors périmètre de rôle : l'unicité de l'email s'apprécie sur tout l'annuaire. */
  private readonly allRows = computed<readonly CollaboratorRow[]>(() =>
    this._collaborators().map((collaborator) => this.toRow(collaborator)),
  );

  /**
   * Lignes de l'annuaire, périmètre du rôle appliqué (RM-PIN-195). Le statut d'équipement est
   * recalculé à partir du matériel détenu : il n'est jamais saisi (RM-PIN-194).
   */
  readonly rows = computed<readonly CollaboratorRow[]>(() => {
    const role = this.session.role();

    return this.allRows().filter((row) => {
      if (role === 'manager') return DEMO_MANAGER_TEAM.includes(row.name);
      if (role === 'collab') return row.name === 'Camille Roussel';
      return true;
    });
  });

  readonly total = computed(() => this.rows().length);
  readonly equippedCount = computed(
    () => this.rows().filter((row) => row.equipmentStatus === 'Équipé').length,
  );
  readonly toEquipCount = computed(
    () => this.rows().filter((row) => row.equipmentStatus === 'À équiper').length,
  );

  /**
   * Rôles métier présents, pour alimenter le filtre de l'écran. Le rôle métier est facultatif à la
   * création : les fiches qui n'en portent pas affichent « — » et ne créent pas d'entrée de filtre.
   */
  readonly jobTitles = computed(() =>
    [...new Set(this.rows().map((row) => row.jobTitle))].filter((job) => job !== '—').sort(),
  );

  /**
   * Managers désignables. Le backend exige un collaborateur **existant** de l'annuaire
   * (ERR-PIN-192) : la liste fermée du formulaire rend ce cas d'erreur inatteignable par la saisie.
   */
  readonly managerCandidates = computed(() =>
    [...this.allRows()].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
  );

  byId(id: string): CollaboratorRow | undefined {
    return this.allRows().find((row) => row.id === id);
  }

  /**
   * Fiche visible dans le périmètre du rôle (RM-PIN-195). Distinguer ce résultat de {@link byId}
   * permet à la fiche collaborateur de séparer « introuvable » de « hors périmètre »
   * (ERR-PIN-195b) sans jamais exposer le contenu d'une fiche interdite.
   */
  scopedById(id: string): CollaboratorRow | undefined {
    return this.rows().find((row) => row.id === id);
  }

  // --- Création (UC-04) --------------------------------------------------------------

  /**
   * Crée une fiche d'annuaire. Les règles reprises ici sont celles du handler livré, dans le même
   * ordre : elles ne sont côté client qu'un confort de saisie, la garantie reste serveur.
   */
  create(
    draft: NewCollaboratorDraft,
  ): { collaborator: CollaboratorRow } | { failure: CreateCollaboratorFailure } {
    // RM-PIN-191 — nom, prénom, email, service, statut RH et date d'arrivée sont obligatoires.
    const missing = [
      ['nom', draft.lastName],
      ['prénom', draft.firstName],
      ['email professionnel', draft.email],
      ['service', draft.departmentId],
      ['statut RH', draft.employmentStatus],
      ["date d'arrivée", draft.arrivalDate],
    ]
      .filter(([, value]) => !value?.trim())
      .map(([field]) => field);

    if (missing.length > 0) {
      return {
        failure: {
          code: 'ERR-PIN-191',
          message: `Champs obligatoires manquants : ${missing.join(', ')}. Complétez la fiche avant d'enregistrer.`,
        },
      };
    }

    const request = toCreateCollaboratorRequest(draft);

    if (!isPlausibleEmail(request.email)) {
      return {
        failure: {
          code: 'ERR-PIN-190',
          message: `L'email professionnel « ${draft.email.trim()} » n'est pas une adresse valide.`,
        },
      };
    }

    // RM-PIN-190 — unicité de l'email sur tout l'annuaire, comparaison insensible à la casse
    // (l'email est normalisé en minuscules par la charge utile, comme l'objet-valeur du domaine).
    const clash = this.allRows().find((row) => row.email === request.email);
    if (clash) {
      return {
        failure: {
          code: 'ERR-PIN-190',
          message:
            `Un collaborateur avec l'email ${request.email} existe déjà dans l'annuaire : ` +
            `${clash.name}. Consultez sa fiche ou corrigez l'email saisi.`,
          existing: clash,
        },
      };
    }

    // Le service doit exister : il fonde le périmètre du responsable de service (RM-PIN-195).
    if (!this.departments.some((department) => department.id === request.departmentId)) {
      return {
        failure: {
          code: 'ERR-PIN-191',
          message: `Service introuvable : ${request.departmentId}. Sélectionnez un service existant.`,
        },
      };
    }

    // Le manager fonde le périmètre du rôle Manager/RH (RM-PIN-192).
    if (request.managerId && !this.byId(request.managerId)) {
      return {
        failure: {
          code: 'ERR-PIN-192',
          message: `Manager introuvable : ${request.managerId}. Sélectionnez un collaborateur de l'annuaire.`,
        },
      };
    }

    const collaborator: Collaborator = {
      // Le backend génère un GUID v7 applicatif ; en local un identifiant unique suffit.
      id: newLocalId(),
      lastName: request.lastName,
      firstName: request.firstName,
      email: request.email,
      departmentId: request.departmentId,
      jobTitle: request.jobTitle ?? '—',
      employmentStatus: draft.employmentStatus,
      arrivalDate: request.arrivalDate,
      managerId: request.managerId ?? null,
      internalNotes: request.internalNotes ?? null,
    };

    this._collaborators.update((collaborators) => [collaborator, ...collaborators]);

    return { collaborator: this.toRow(collaborator) };
  }

  /**
   * Ligne d'affichage d'une fiche : libellé, service et manager résolus, compteurs par famille et
   * statut d'équipement recalculé (RM-PIN-194).
   */
  private toRow(collaborator: Collaborator): CollaboratorRow {
    const name = displayName(collaborator);
    const held = this.inventory.heldBy(name);
    const countFamily = (family: AssetFamily) =>
      held.filter((asset) => familyOf(asset.category) === family).length;

    const computers = countFamily('poste de travail');
    const phones = countFamily('mobile');
    const total = held.length;

    const manager = this._collaborators().find(
      (candidate) => candidate.id === collaborator.managerId,
    );

    return {
      ...collaborator,
      name,
      department:
        this.departments.find((department) => department.id === collaborator.departmentId)?.name ??
        '—',
      managerName: manager ? displayName(manager) : null,
      computers,
      phones,
      others: total - computers - phones,
      total,
      equipmentStatus: computeEquipmentStatus(computers, total),
      initials: name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
      departedWithEquipment: collaborator.employmentStatus === 'Parti' && total > 0,
    };
  }
}

/**
 * Traduit la saisie du formulaire en charge utile du backend : espaces retirés, email normalisé en
 * minuscules (RM-PIN-190), statut RH converti en code de l'énumération (RM-PIN-197), champs
 * facultatifs laissés à `null` plutôt qu'à la chaîne vide.
 */
export function toCreateCollaboratorRequest(draft: NewCollaboratorDraft): CreateCollaboratorRequest {
  return {
    lastName: draft.lastName.trim(),
    firstName: draft.firstName.trim(),
    email: draft.email.trim().toLowerCase(),
    departmentId: draft.departmentId,
    employmentStatus: EMPLOYMENT_STATUS_CODES[draft.employmentStatus],
    arrivalDate: draft.arrivalDate,
    managerId: draft.managerId || null,
    jobTitle: draft.jobTitle.trim() || null,
    internalNotes: draft.internalNotes.trim() || null,
  };
}

/**
 * RM-PIN-194, **règle de repli** appliquée en l'absence de dotation cible : « Équipé » suppose au
 * moins un poste de travail (PC portable ou PC fixe) et au moins un périphérique.
 *
 * ⚠️ La maquette v5 code une règle plus permissive (« ≥ 1 ordinateur portable et total ≥ 2 ») qui
 * compte un second ordinateur comme un périphérique. C'est la spec qui fait foi, donc c'est elle qui
 * est implémentée ici — écart assumé et signalé.
 */
export function computeEquipmentStatus(workstations: number, total: number): EquipmentStatus {
  if (total === 0) {
    return 'À équiper';
  }

  const peripherals = total - workstations;
  return workstations > 0 && peripherals > 0 ? 'Équipé' : 'Partiel';
}

/** Identifiant local d'une fiche saisie. Format GUID pour rester substituable au GUID v7 du backend. */
function newLocalId(): string {
  return crypto.randomUUID();
}
